import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSecurityDeviceVulnerabilities } from '../../api/internal/usersInternalApi';
import {
    Card, CardTitle, Button, IconButton, Pill, Input, Select, FieldLabel,
    Notice, EmptyState, Spinner, SpinnerContainer, DataTable, TableScroll,
} from '../ui';
import {
    FilterBar, ClickableRow, MonoCell, Muted, Pager,
} from './security.styled';
import { SeverityPill, SEVERITY_ORDER, formatEpss, formatDate } from './severity';
import PathCell from './pathCell';
import { downloadCsv, csvNumber } from './csvDownload';

// Une page de tableau. Mesuré le 2026-09-02 sur la préproduction : le poste le
// plus vulnérable du parc porte 3 182 CVE, la médiane des postes vulnérables
// est à 376, et 45 postes dépassent le millier. Tout rendre d'un coup ferait
// 25 000 cellules et un scroll qui accroche ; 100 lignes se rendent sans délai
// perceptible et l'export couvre de toute façon la liste entière.
const PAGE_SIZE = 100;

// Priorité d'affichage : probabilité d'exploitation d'abord, score CVSS en
// départage. Même critère que l'aperçu servi par /api/device-overview/:key, pour
// que les dix premières lignes de la liste complète soient exactement les dix
// de l'aperçu — l'endpoint des vulnérabilités trie par sévérité, ce qui aurait
// donné deux ordres différents dans une seule carte.
function byPriority(a, b) {
    return (b.epssScore || 0) - (a.epssScore || 0) || (b.cvssScore || 0) - (a.cvssScore || 0);
}

function VulnRow({ vulnerabilite, onOpen, showDiskPaths, showRegistryPaths }) {
    return (
        <ClickableRow onClick={onOpen}>
            <td><MonoCell>{vulnerabilite.cveCode}</MonoCell></td>
            <td><SeverityPill severity={vulnerabilite.severity} /></td>
            <td>{vulnerabilite.cvssScore ?? '—'}</td>
            <td>{formatEpss(vulnerabilite.epssScore)}</td>
            <td>
                {vulnerabilite.exploitabilityLevel === 'PoC'
                    ? <Pill $tone="danger">Oui</Pill>
                    : <Muted>—</Muted>}
            </td>
            <td><Muted>{(vulnerabilite.software || []).join(', ') || '—'}</Muted></td>
            {showDiskPaths && <td><PathCell paths={vulnerabilite.diskPaths} /></td>}
            {showRegistryPaths && <td><PathCell paths={vulnerabilite.registryPaths} /></td>}
        </ClickableRow>
    );
}

/**
 * Section « Vulnérabilités » de la fiche appareil : l'aperçu des dix plus
 * prioritaires, dépliable sur la liste complète, et un export CSV.
 *
 * La liste complète n'est PAS dans la réponse de la fiche : elle pèse jusqu'à
 * 1,3 Mo pour un seul poste, ce qui alourdirait l'ouverture de chaque fiche pour
 * une donnée que l'on ne déplie pas systématiquement. Elle est donc chargée à la
 * demande depuis /api/defender/devices/:deviceId/vulnerabilities, une seule fois
 * par fiche — l'export réutilise le même chargement.
 */
export default function DeviceVulnerabilities({ vulnerabilities, deviceId, hostname }) {
    const navigate = useNavigate();

    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [search, setSearch] = useState('');
    const [severity, setSeverity] = useState('');
    const [exploitOnly, setExploitOnly] = useState(false);
    const [page, setPage] = useState(1);

    // Un seul chargement sert le dépliage ET l'export. La promesse est retenue
    // et non seulement son résultat : déplier puis exporter aussitôt tomberait
    // sinon dans le trou entre les deux, et referait un appel de 1,6 Mo.
    const chargement = useRef(null);

    const charger = () => {
        if (rows) return Promise.resolve(rows);
        if (chargement.current) return chargement.current;

        setLoading(true);
        setError(null);
        chargement.current = getSecurityDeviceVulnerabilities(deviceId)
            .then((res) => {
                const liste = (res?.vulnerabilities || []).slice().sort(byPriority);
                setRows(liste);
                return liste;
            })
            .catch(() => {
                setError('Chargement de la liste complète impossible.');
                return null;
            })
            .finally(() => { setLoading(false); chargement.current = null; });
        return chargement.current;
    };

    const basculer = () => {
        if (!expanded) {
            charger();
        } else {
            // Les filtres disparaissent avec la liste : les laisser actifs sous
            // un aperçu de dix lignes ferait mentir l'export, qui exporte ce que
            // l'utilisateur voit.
            setSearch('');
            setSeverity('');
            setExploitOnly(false);
            setPage(1);
        }
        setExpanded((o) => !o);
    };

    const exporter = () => {
        setExporting(true);
        charger()
            .then((liste) => {
                if (!liste) return;
                // Ce que l'utilisateur voit est ce qu'il exporte : les filtres
                // s'appliquent au fichier. Sans cela, filtrer sur « Critical »
                // puis exporter produirait un fichier de 3 182 lignes dont rien
                // ne dirait qu'il ne correspond pas à l'écran.
                const exportees = filtrer(liste);
                if (exportees.length === 0) return;
                downloadCsv(
                    `${hostname || 'appareil'}_vulnerabilites.csv`,
                    [
                        'Appareil', 'CVE', 'Sévérité', 'Score CVSS',
                        "Probabilité d'exploitation (EPSS)", 'Exploit connu',
                        'Publiée le', 'Logiciels', 'Correctif recommandé',
                        'Chemins disque', 'Chemins registre',
                    ],
                    exportees.map((v) => [
                        hostname || '',
                        v.cveCode,
                        v.severity || '',
                        csvNumber(v.cvssScore),
                        csvNumber(v.epssScore),
                        v.exploitabilityLevel === 'PoC' ? 'Oui' : 'Non',
                        formatDate(v.publishedOn),
                        (v.software || []).join(' | '),
                        v.recommendedSecurityUpdate || '',
                        (v.diskPaths || []).join(' | '),
                        (v.registryPaths || []).join(' | '),
                    ])
                );
            })
            .finally(() => setExporting(false));
    };

    // Même filtre pour l'écran et pour l'export : c'est ce qui garantit que le
    // fichier contient exactement ce que l'utilisateur a sous les yeux.
    const filtrer = useCallback((liste) => {
        const terme = search.trim().toLowerCase();
        return liste.filter((v) => {
            if (severity && v.severity !== severity) return false;
            if (exploitOnly && v.exploitabilityLevel !== 'PoC') return false;
            if (!terme) return true;
            return v.cveCode.toLowerCase().includes(terme)
                || (v.software || []).some((s) => String(s).toLowerCase().includes(terme));
        });
    }, [search, severity, exploitOnly]);

    // Le filtrage traverse jusqu'à 3 182 lignes : mémorisé pour ne pas le
    // refaire à chaque rendu de la fiche.
    const filtered = useMemo(() => (rows ? filtrer(rows) : []), [rows, filtrer]);

    if (!vulnerabilities.available || vulnerabilities.total === 0) return null;

    const affichables = expanded && rows ? filtered : (vulnerabilities.top || []);
    const totalPages = Math.max(Math.ceil(affichables.length / PAGE_SIZE), 1);
    const pageCourante = Math.min(page, totalPages);
    const visibles = expanded && rows
        ? affichables.slice((pageCourante - 1) * PAGE_SIZE, pageCourante * PAGE_SIZE)
        : affichables;

    // Chaque colonne de chemins est décidée séparément, pas les deux ensemble :
    // mesuré le 2026-08-27, les chemins disque sont présents sur toutes les
    // lignes d'un poste Windows ayant des CVE, alors que les chemins registre
    // n'existent que sur un poste sur six. Une condition commune afficherait
    // donc le plus souvent une colonne registre entièrement remplie de tirets.
    // Un poste Linux n'a ni l'un ni l'autre : les deux colonnes disparaissent.
    //
    // Décidé sur l'ensemble affichable et non sur la page courante : sinon une
    // page sans aucun chemin registre escamotait la colonne, et le tableau
    // changeait de forme d'une page à l'autre.
    const showDiskPaths = affichables.some((v) => v.diskPaths?.length > 0);
    const showRegistryPaths = affichables.some((v) => v.registryPaths?.length > 0);

    const filtreActif = Boolean(search.trim() || severity || exploitOnly);
    const majFiltre = (setter) => (value) => { setter(value); setPage(1); };

    return (
        <Card $pad="16px">
            <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {expanded ? 'Toutes les vulnérabilités' : 'Vulnérabilités prioritaires'}
                <Muted style={{ fontWeight: 400, fontSize: '0.78rem' }}>
                    {expanded
                        ? `${vulnerabilities.total.toLocaleString('fr-FR')} au total, triées par probabilité d'exploitation`
                        : `10 sur ${vulnerabilities.total.toLocaleString('fr-FR')}, triées par probabilité d'exploitation`}
                </Muted>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                    <Button $variant="secondary" onClick={basculer} disabled={loading}>
                        {loading && !expanded
                            ? <Spinner $size="14px" />
                            : <i className={expanded ? 'bx bx-chevron-up' : 'bx bx-chevron-down'} />}
                        {expanded
                            ? 'Réduire aux 10 prioritaires'
                            : `Afficher les ${vulnerabilities.total.toLocaleString('fr-FR')}`}
                    </Button>
                    <Button
                        $variant="secondary"
                        onClick={exporter}
                        disabled={exporting}
                        title={filtreActif
                            ? `Exporte les ${filtered.length.toLocaleString('fr-FR')} lignes correspondant aux filtres`
                            : `Exporte les ${vulnerabilities.total.toLocaleString('fr-FR')} vulnérabilités de ce poste`}
                    >
                        {exporting ? <Spinner $size="14px" /> : <i className="bx bxs-download" />}
                        Exporter en CSV
                    </Button>
                </div>
            </CardTitle>

            {error && (
                <Notice $tone="danger" style={{ marginBottom: '12px' }}>
                    <i className="bx bx-error-circle" />
                    {error} L'aperçu ci-dessous reste disponible.
                </Notice>
            )}

            {expanded && rows && (
                <FilterBar style={{ marginBottom: '14px' }}>
                    <div style={{ flex: 1, minWidth: '220px' }}>
                        <FieldLabel htmlFor="vuln-search">Recherche</FieldLabel>
                        <Input
                            id="vuln-search"
                            style={{ width: '100%', marginTop: '6px' }}
                            placeholder="Code CVE ou logiciel..."
                            value={search}
                            onChange={(e) => majFiltre(setSearch)(e.target.value)}
                        />
                    </div>
                    <div>
                        <FieldLabel htmlFor="vuln-severity">Sévérité</FieldLabel>
                        <Select
                            id="vuln-severity"
                            style={{ marginTop: '6px' }}
                            value={severity}
                            onChange={(e) => majFiltre(setSeverity)(e.target.value)}
                        >
                            <option value="">Toutes</option>
                            {SEVERITY_ORDER.map((s) => <option key={s} value={s}>{s}</option>)}
                        </Select>
                    </div>
                    <label htmlFor="vuln-exploit">
                        <input
                            id="vuln-exploit"
                            type="checkbox"
                            checked={exploitOnly}
                            onChange={(e) => majFiltre(setExploitOnly)(e.target.checked)}
                        />
                        Exploit connu uniquement
                    </label>
                </FilterBar>
            )}

            {loading && expanded ? (
                <SpinnerContainer><Spinner /></SpinnerContainer>
            ) : visibles.length === 0 ? (
                <EmptyState>
                    <i className="bx bx-search-alt" />
                    <h3>Aucune vulnérabilité</h3>
                    <p>Aucune vulnérabilité de ce poste ne correspond à ces critères.</p>
                </EmptyState>
            ) : (
                <>
                    <TableScroll>
                        <DataTable>
                            <thead>
                                <tr>
                                    <th>CVE</th>
                                    <th>Sévérité</th>
                                    <th>CVSS</th>
                                    <th>EPSS</th>
                                    <th>Exploit</th>
                                    <th>Logiciel</th>
                                    {showDiskPaths && <th>Chemins disque</th>}
                                    {showRegistryPaths && <th>Chemins registre</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {visibles.map((v) => (
                                    <VulnRow
                                        key={v.cveCode}
                                        vulnerabilite={v}
                                        onOpen={() => navigate(`/Security/Vulnerabilities?cve=${v.cveCode}`)}
                                        showDiskPaths={showDiskPaths}
                                        showRegistryPaths={showRegistryPaths}
                                    />
                                ))}
                            </tbody>
                        </DataTable>
                    </TableScroll>

                    {expanded && rows && (
                        <Pager>
                            <span>
                                <strong>{filtered.length.toLocaleString('fr-FR')}</strong>
                                {filtreActif ? ` vulnérabilités sur ${vulnerabilities.total.toLocaleString('fr-FR')}` : ' vulnérabilités'}
                            </span>
                            <div className="spacer" />
                            <span>Page <strong>{pageCourante}</strong> / {totalPages}</span>
                            <IconButton
                                $bordered
                                disabled={pageCourante <= 1}
                                onClick={() => setPage(pageCourante - 1)}
                                title="Page précédente"
                            >
                                <i className="bx bx-chevron-left" />
                            </IconButton>
                            <IconButton
                                $bordered
                                disabled={pageCourante >= totalPages}
                                onClick={() => setPage(pageCourante + 1)}
                                title="Page suivante"
                            >
                                <i className="bx bx-chevron-right" />
                            </IconButton>
                        </Pager>
                    )}
                </>
            )}
        </Card>
    );
}
